import React, { useEffect, useCallback, useState, useRef } from "react";
import {
  List,
  Button,
  Spin,
  Typography,
  message,
  Input,
  Layout,
  Modal,
  Select,
  Popover,
} from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { mapService } from "../../services/map.service";
import { valveService } from "../../services/valve.service";
import { userService } from "../../services/user.service";
import PasteImage from "../../components/PasteImage";
import "./TencentMap.scss";

// 全局变量，用于存储当前年份
let this_Year = 2025;
let skipRequest = 0;

function TencentMap() {
  // 常州市中心坐标
  const czCoordinates = { lat: 31.81073, lng: 119.97365 };

  // 阀门列表状态
  const [valveList, setValveList] = useState([]);
  const [currentYear, setCurrentYear] = useState(2025);
  const [loading, setLoading] = useState(true);
  const [selectedValve, setSelectedValve] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("address");
  const [isDispatchModalVisible, setIsDispatchModalVisible] = useState(false);
  const [userNames, setUserNames] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dispatchValve, setDispatchValve] = useState(null);
  const [isListCollapsed, setIsListCollapsed] = useState(false);

  // 添加筛选状态
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [filterDistrict, setFilterDistrict] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);

  // 切换年份处理函数
  const handleYearSwitch = async () => {
    const newYear = currentYear === 2025 ? 2023 : 2025;
    setCurrentYear(newYear);
    this_Year = newYear;

    // 清空筛选状态
    setSelectedDistricts([]);
    setSelectedStatuses([]);
    setFilterDistrict("");
    setFilterStatus("");

    if (mapRef.current) {
      const center = mapRef.current.getCenter();

      // 获取当前地图比例尺
      const getscale = mapRef.current.getControl(
        window.TMap.constants.DEFAULT_CONTROL_ID.SCALE
      ).dom.innerText;
      const cleanedScale = getscale.replace(/\s+/g, "");

      // 判断单位并提取数值
      let scaleInMeters;

      if (cleanedScale.includes("公里")) {
        scaleInMeters = parseFloat(cleanedScale.split("公里")[0]) * 1000; // 转换为米
      } else if (cleanedScale.includes("米")) {
        scaleInMeters = parseFloat(cleanedScale.split("米")[0]); // 已经是米
      } else {
        scaleInMeters = 0;
      }

      // 根据比例尺大小决定是否获取和显示阀门
      if (scaleInMeters > 1000) {
        // 比例尺超过1000米，不请求后台接口，隐藏阀门标记
        if (valveMarkersRef.current) {
          valveMarkersRef.current.setMap(null);
        }
        setValveList([]);
      } else {
        try {
          const response = await mapService.getValveListByGeo(
            center.lng,
            center.lat,
            1000,
            this_Year
          );
          if (response.code === 0) {
            setValveList(response.data);
            updateValveMarkers(mapRef.current, response.data, scaleInMeters);
          }
        } catch (error) {
          console.error("获取阀门数据失败:", error);
          message.error("获取阀门数据失败");
        }
      }
    }
  };

  // 阀门状态图标映射
  const ValvesIconMap = {
    1: "https://cdn.czwatersystem.com/static_res/zf_1_dot.png",
    2: "https://cdn.czwatersystem.com/static_res/zf_2_dot.png",
    3: "https://cdn.czwatersystem.com/static_res/zf_3_dot.png",
    4: "https://cdn.czwatersystem.com/static_res/zf_4_dot.png",
    5: "https://cdn.czwatersystem.com/static_res/zf_5_dot.png",
    6: "https://cdn.czwatersystem.com/static_res/zf_6_dot.png",
    7: "https://cdn.czwatersystem.com/static_res/zf_7_dot.png",
  };

  // 蝶阀状态图标映射
  const ButterflyValvesIconMap = {
    1: "https://cdn.czwatersystem.com/static_res/df_1_dot.png",
    2: "https://cdn.czwatersystem.com/static_res/df_2_dot.png",
    3: "https://cdn.czwatersystem.com/static_res/df_3_dot.png",
    4: "https://cdn.czwatersystem.com/static_res/df_4_dot.png",
    5: "https://cdn.czwatersystem.com/static_res/df_5_dot.png",
    6: "https://cdn.czwatersystem.com/static_res/df_6_dot.png",
    7: "https://cdn.czwatersystem.com/static_res/df_7_dot.png",
  };

  // 阀门类别映射
  const valveCategoryMap = {
    1: "小区进水阀门",
    2: "小区环线阀门",
    3: "小区楼幢阀门",
    4: "小区单元阀门",
    5: "小区消火栓阀门",
    6: "小区其他",
    7: "市政管线阀门",
    8: "市政管线消防栓阀门",
  };

  const cylbDescMap = {
    0:'开井查验',
    1:'不开井查验'
  }

  // 阀门类型映射
  const valveTypeMap = {
    1: "闸阀",
    2: "蝶阀",
  };

  // 井盖位置映射
  const wellCoverLocationsMap = {
    1: "机动车道",
    2: "非机动车道",
    3: "人行横道",
    4: "人行道",
    5: "绿化带",
    6: "斑马线",
    7: "泥土路面",
    8: "铺装道板",
  };

  // 井盖异常描述映射
  const wellCoverAbnormalDescMap = {
    1: "无",
    2: "GIS有，现场灭失",
    3: "GIS有，现场错位",
    4: "GIS无，现场有",
  };

  // 阀门状态映射及颜色
  const valveStatusConfig = {
    1: { text: "待普查", color: "#339DE2" },
    2: { text: "普查中", color: "#FBB531" },
    3: { text: "已普查", color: "#79CE7E" },
    4: { text: "异常挂起", color: "#EB5F5D" },
    5: { text: "已签证", color: "#B18D80" },
    6: { text: "已结算", color: "#9AB4BF" },
    7: { text: "已挂接", color: "#0D4480" },
  };

  // 地图初始化配置
  const getMapOptions = () => {
    if (!window.TMap) {
      console.error("TMap SDK未加载");
      return null;
    }
    return {
      center: new window.TMap.LatLng(czCoordinates.lat, czCoordinates.lng),
      zoom: 16,
      maxZoom:23,
      viewMode: "2D",
      baseMap: {
        type: "vector",
        features: ["base", "building2d", "label", "point"],
      },
    };
  };

  // 防抖处理中心点变更
  const handleCenterChanged = useCallback(
    debounce(async (map) => {
      // 如果标志为true，跳过API请求
      if (skipRequest) {
        skipRequest = 0;
        console.log("Skipped API request after valve click");
        return;
      }

      const center = map.getCenter();

      // 获取当前地图比例尺
      const getscale = map.getControl(
        window.TMap.constants.DEFAULT_CONTROL_ID.SCALE
      ).dom.innerText;
      const cleanedScale = getscale.replace(/\s+/g, "");

      // 判断单位并提取数值
      let scaleInMeters;

      if (cleanedScale.includes("公里")) {
        scaleInMeters = parseFloat(cleanedScale.split("公里")[0]) * 1000;
      } else if (cleanedScale.includes("米")) {
        scaleInMeters = parseFloat(cleanedScale.split("米")[0]);
      } else {
        scaleInMeters = 0;
      }

      // 根据比例尺大小决定是否获取和显示阀门
      if (scaleInMeters > 1000) {
        // 比例尺超过1000米，不请求后台接口，隐藏阀门标记
        if (valveMarkersRef.current) {
          valveMarkersRef.current.setMap(null);
        }
        setValveList([]);
      } else {
        try {
          // 使用函数形式获取最新的状态值
          const getLatestState = () => {
            // 这里需要手动获取最新的状态值，因为debounce会导致闭包捕获旧值
            const currentFilterDistrict =
              document
                .getElementById("current-filter-district")
                ?.getAttribute("data-value") || "";
            const currentFilterStatus =
              document
                .getElementById("current-filter-status")
                ?.getAttribute("data-value") || "";
            const currentSearchType =
              document
                .getElementById("current-search-type")
                ?.getAttribute("data-value") || "address";
            const currentSearchValue =
              document
                .getElementById("current-search-value")
                ?.getAttribute("data-value") || "";

            return {
              filterDistrict: currentFilterDistrict,
              filterStatus: currentFilterStatus,
              searchType: currentSearchType,
              searchValue: currentSearchValue,
            };
          };

          const latestState = getLatestState();

          // 获取流水号搜索参数
          const fmlshParam =
            latestState.searchType === "serialNumber" && latestState.searchValue
              ? latestState.searchValue.trim()
              : "";
            
          fetchValvesByGeo(
            center.lng,
            center.lat,
            1000,
            latestState.filterDistrict,
            latestState.filterStatus,
            fmlshParam
          );
        } catch (error) {
          console.error("获取阀门数据失败:", error);
        }
      }
    }, 500),
    [] // 移除依赖，因为我们手动获取最新状态
  );

  // 加载Mock数据
  useEffect(() => {
    // 模拟API请求延迟
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let map = null;
    let mapScript = document.querySelector(
      'script[src*="map.qq.com/api/gljs"]'
    );
    let initAttempts = 0;
    const maxAttempts = 3;

    const initMap = async () => {
      try {
        // 确保SDK已加载
        if (!window.TMap) {
          if (initAttempts < maxAttempts) {
            console.log(`等待SDK加载，尝试次数: ${initAttempts + 1}`);
            initAttempts++;
            setTimeout(initMap, 500);
            return;
          }
          throw new Error("TMap SDK加载超时");
        }

        // 检查是否已存在地图实例
        const existingMap = document.querySelector(
          "#map-container .tmap-container"
        );
        if (existingMap) {
          console.log("地图实例已存在，跳过初始化");
          return;
        }

        const options = getMapOptions();
        if (!options) {
          throw new Error("无法获取地图配置");
        }

        // 初始化地图
        map = new window.TMap.Map("map-container", options);
        mapRef.current = map; // 保存地图实例引用
        map.removeControl(window.TMap.constants.DEFAULT_CONTROL_ID.ROTATION);
        // 创建中心点标记
        map
          .getControl(window.TMap.constants.DEFAULT_CONTROL_ID.SCALE)
          .setPosition(window.TMap.constants.CONTROL_POSITION.TOP_CENTER);
        map
          .getControl(window.TMap.constants.DEFAULT_CONTROL_ID.ZOOM)
          .setPosition(window.TMap.constants.CONTROL_POSITION.BOTTOM_RIGHT);

        
        const centerMarker = new window.TMap.MultiMarker({
          map: map,
          styles: {
            center: new window.TMap.MarkerStyle({
              width: 25,
              height: 25,
              anchor: { x: 12.5, y: 12.5 },
              src: "https://cdn.czwatersystem.com/static_res/location_dot_center.png",
            }),
          },
          geometries: [
            {
              id: "center_marker",
              styleId: "center",
              position: new window.TMap.LatLng(
                czCoordinates.lat,
                czCoordinates.lng
              ),
            },
          ],
        });

        // 添加中心点变更事件监听
        map.on("center_changed", () => {
          handleCenterChanged(map);
          // 更新标记位置
          const center = map.getCenter();
          centerMarker.updateGeometries([
            {
              id: "center_marker",
              styleId: "center",
              position: center,
            },
          ]);
        });

        // 添加缩放事件监听（使用防抖，1.5秒后触发）
        map.on(
          "zoom_changed",
          debounce(() => {
            // 获取当前地图比例尺
            const getscale = map.getControl(
              window.TMap.constants.DEFAULT_CONTROL_ID.SCALE
            ).dom.innerText;

            // 处理比例尺文本：去除空格和换行
            const cleanedScale = getscale.replace(/\s+/g, "");

            // 判断单位并提取数值
            let scaleValue;
            let scaleInMeters;
            let scaleUnit;
            if (cleanedScale.includes("公里")) {
              // 单位是公里，提取数值并转换为米
              scaleUnit = "公里";
              scaleValue = parseFloat(cleanedScale.split("公里")[0]);
              scaleInMeters = scaleValue * 1000; // 转换为米
            } else if (cleanedScale.includes("米")) {
              // 单位是米，直接提取数值
              scaleUnit = "米";
              scaleValue = parseFloat(cleanedScale.split("米")[0]);
              scaleInMeters = scaleValue; // 已经是米，不需要转换
            } else {
              // 未识别的单位
              return;
            }

            const center = map.getCenter();

            // 根据比例尺大小控制阀门图标的显示逻辑
            if (scaleInMeters >= 1000) {
              // 比例尺超过200米，不请求后台接口，隐藏阀门标记
              message.info('当前地图展示标尺超过1公里，缩放后可展示阀门');
              if (valveMarkersRef.current) {
                valveMarkersRef.current.setMap(null);
              }
              setValveList([]);
            } else {
            }
          }, 1000)
        );

        // 验证初始化后的地图中心点
        const initialCenter = map.getCenter();
        console.log("地图初始化中心点:", {
          lat: initialCenter.lat,
          lng: initialCenter.lng,
        });

        // 初始化完成后获取附近阀门数据
        try {
          // 获取当前地图比例尺
          const getscale = map.getControl(
            window.TMap.constants.DEFAULT_CONTROL_ID.SCALE
          ).dom.innerText;
          const cleanedScale = getscale.replace(/\s+/g, "");
          let scaleInMeters;
          if (cleanedScale.includes("公里")) {
            scaleInMeters = parseFloat(cleanedScale.split("公里")[0]) * 1000;
          } else if (cleanedScale.includes("米")) {
            scaleInMeters = parseFloat(cleanedScale.split("米")[0]);
          } else {
            scaleInMeters = 0;
          }

          // 根据比例尺大小决定是否获取和显示阀门
          if (scaleInMeters <= 1000) {
            // 使用封装的函数获取阀门数据
            const fmlshParam =
              searchType === "serialNumber" && searchValue
                ? searchValue.trim()
                : "";
            fetchValvesByGeo(
              initialCenter.lng,
              initialCenter.lat,
              1000,
              "",
              "",
              fmlshParam
            );
          } else {
            // 比例尺大于200米，不显示阀门
            setValveList([]);
          }
          setLoading(false);
        } catch (error) {
          console.error("获取阀门数据失败:", error);
          message.error("获取阀门数据失败");
        }
      } catch (error) {
        console.error("地图初始化失败:", error);
      }
    };

    if (!mapScript) {
      mapScript = document.createElement("script");
      mapScript.src = `https://map.qq.com/api/gljs?v=1.exp&key=6C4BZ-LVJCQ-OZX55-BDMBZ-W5QMZ-E7BTZ`;
      mapScript.async = true;
      mapScript.onload = initMap;
      mapScript.onerror = () => console.error("腾讯地图SDK加载失败");
      document.head.appendChild(mapScript);
    } else {
      // 如果脚本已加载，直接初始化地图
      initMap();
    }

    return () => {
      // 清理地图实例和事件监听
      if (map) {
        map.destroy();
        map = null;
      }
    };
  }, []);

  // 存储地图实例的引用
  const mapRef = useRef(null);
  // 存储阀门标记的引用
  const valveMarkersRef = useRef(null);

  // 更新地图上的阀门标记
  const updateValveMarkers = (map, valves, scaleInMeters = 0) => {
    try {
      // 检查参数有效性
      if (!map || !valves || !Array.isArray(valves) || valves.length === 0) {
        console.log("无效的地图或阀门数据");
        return;
      }

      // 如果已存在标记，先清除
      if (valveMarkersRef.current) {
        valveMarkersRef.current.setMap(null);
      }

      // 创建阀门标记样式
      const styles = {};

      // 根据比例尺大小设置图标尺寸
      let iconWidth, iconHeight, anchorX, anchorY;
      let iconUrl;

      if (scaleInMeters >= 500){
        // 小号图标 (500米以上)
        iconWidth = 10;
        iconHeight = 12;
        anchorX = 5;
        anchorY = 12;
        iconUrl = 'https://cdn.czwatersystem.com/static_res/minilocation_blue.png';
      }
      else if (scaleInMeters >= 200 && scaleInMeters < 500) {
        // 中小号图标 (200-500米)
        iconWidth = 20;
        iconHeight = 22;
        anchorX = 10;
        anchorY = 22;
      }
      else if (scaleInMeters > 50 && scaleInMeters < 200) {
        // 中号图标 (50-200)
        iconWidth = 25;
        iconHeight = 28;
        anchorX = 12.5;
        anchorY = 28;
      } else {
        // 大号图标 (50米内)
        iconWidth = 30;
        iconHeight = 34;
        anchorX = 15;
        anchorY = 33;
      }

      // 过滤并验证有效阀门数据
      const validValves = valves.filter(valve => 
        valve && 
        valve.wgs84_lat && 
        valve.wgs84_lon && 
        !isNaN(parseFloat(valve.wgs84_lat)) && 
        !isNaN(parseFloat(valve.wgs84_lon))
      );

      if (validValves.length === 0) {
        console.log("没有有效的阀门坐标数据");
        return;
      }

      // 根据阀门状态和类型动态创建样式
      validValves.forEach((valve) => {
        const status = valve.status || 1; // 默认状态为1（待普查）
        const subtype = valve.subtype || 1; // 默认类型为1（闸阀）
        const styleId = `valve_style_${subtype}_${status}`;

        // 如果样式不存在，则创建
        if (!styles[styleId]) {
          try {
            // 当比例尺大于等于500米时，统一使用小型图标
            let markerIconUrl = '';
            
            if (scaleInMeters >= 500) {
              markerIconUrl = iconUrl; // 使用小型图标URL
            } else {
              // 检查映射中是否存在对应图标
              if (subtype === 2 && ButterflyValvesIconMap[status]) {
                markerIconUrl = ButterflyValvesIconMap[status];
              } else if (ValvesIconMap[status]) {
                markerIconUrl = ValvesIconMap[status];
              } else {
                // 使用默认图标
                markerIconUrl = 'https://cdn.czwatersystem.com/static_res/zf_1_dot.png';
              }
            }

            styles[styleId] = new window.TMap.MarkerStyle({
              width: iconWidth,
              height: iconHeight,
              anchor: { x: anchorX, y: anchorY },
              src: markerIconUrl,
            });
          } catch (error) {
            console.error(`创建样式失败: ${styleId}`, error);
          }
        }
      });

      // 检查是否成功创建了样式
      if (Object.keys(styles).length === 0) {
        console.log("未能创建有效的标记样式");
        return;
      }

      // 创建阀门标记几何体
      const geometries = validValves.map((valve) => {
        try {
          return {
            id: valve.id || `valve_${Math.random().toString(36).substr(2, 9)}`,
            styleId: `valve_style_${valve.subtype || 1}_${valve.status || 1}`,
            position: new window.TMap.LatLng(
              parseFloat(valve.wgs84_lat), 
              parseFloat(valve.wgs84_lon)
            ),
            properties: { ...valve }
          };
        } catch (error) {
          console.error(`创建几何体失败: ${valve.id}`, error);
          return null;
        }
      }).filter(geo => geo !== null); // 过滤掉创建失败的几何体

      // 如果没有有效的几何体数据，则不创建标记
      if (geometries.length === 0) {
        console.log("没有有效的几何体数据");
        return;
      }

      // 创建多标记实例
      try {
        valveMarkersRef.current = new window.TMap.MultiMarker({
          map: map,
          styles: styles,
          geometries: geometries,
        });

        // 添加点击和hover事件
        valveMarkersRef.current.on("click", (evt) => {
          try {
            if (!evt || !evt.geometry || !evt.geometry.properties) {
              console.error("点击事件缺少必要属性");
              return;
            }
            
            const valve = evt.geometry.properties;
            const marker = evt.geometry;
            
            // 检查必要属性
            if (!valve || !marker) {
              console.error("无效的阀门或标记数据");
              return;
            }
            
            // 创建放大的样式
            const status = valve.status || 1;
            const subtype = valve.subtype || 1;
            
            let iconUrl = '';
            if (subtype === 2 && ButterflyValvesIconMap[status]) {
              iconUrl = ButterflyValvesIconMap[status];
            } else if (ValvesIconMap[status]) {
              iconUrl = ValvesIconMap[status];
            } else {
              iconUrl = 'https://cdn.czwatersystem.com/static_res/zf_1_dot.png';
            }

            const enlargedStyle = new window.TMap.MarkerStyle({
              width: 50,
              height: 57,
              anchor: { x: 25, y: 56 },
              src: iconUrl,
            });

            // 更新所有标记为正常大小
            valveMarkersRef.current.updateGeometries(
              geometries.map((geo) => ({
                ...geo,
                styleId: geo.styleId.replace("_enlarged", ""),
              }))
            );

            // 更新当前点击的标记样式
            valveMarkersRef.current.updateGeometries([
              {
                ...marker,
                styleId: `${marker.styleId.replace("_enlarged", "")}_enlarged`,
              },
            ]);

            // 添加放大样式到样式集合
            valveMarkersRef.current.setStyles({
              ...styles,
              [`${marker.styleId.replace("_enlarged", "")}_enlarged`]: enlargedStyle,
            });

            // 如果列表是收起状态，先展开列表
            if (isListCollapsed) {
              setIsListCollapsed(false);
            }

            setSelectedValve(valve);
            setShowDetailView(true);
          } catch (error) {
            console.error("处理点击事件失败", error);
          }
        });
      } catch (error) {
        console.error("创建标记实例失败", error);
      }
    } catch (error) {
      console.error("更新阀门标记失败", error);
    }
  };

  // 组件初始化时获取用户数据
  useEffect(() => {
    verifyUser();
    fetchNicknameOptions();
  }, []);

  // 点击阀门卡片时显示详情
  const handleValveClick = (valve) => {
    // 不再跳转到详情页，而是将地图中心点移动到阀门坐标
    if (mapRef.current && valve.wgs84_lat && valve.wgs84_lon) {
      // 设置标志，阻止下一次center_changed事件触发API请求
      skipRequest = 1;
      // 移动地图中心点
      const valvePosition = new window.TMap.LatLng(
        valve.wgs84_lat,
        valve.wgs84_lon
      );
      mapRef.current.setCenter(valvePosition);

      // 设置选中的阀门，但不显示详情视图
      setSelectedValve(valve);

      // 放大对应的阀门图标
      if (valveMarkersRef.current) {
        // 获取当前所有标记
        const geometries = valveMarkersRef.current.getGeometries();
        // 创建放大的样式
        const styles = valveMarkersRef.current.getStyles();
        const updatedStyles = { ...styles };

        // 先将所有标记恢复正常大小
        const normalGeometries = geometries.map((geo) => {
          // 移除可能存在的_enlarged后缀
          return {
            ...geo,
            styleId: geo.styleId.replace("_enlarged", ""),
          };
        });

        // 更新当前点击的阀门标记样式
        const updatedGeometries = normalGeometries.map((geo) => {
          if (geo.properties && geo.properties.id === valve.id) {
            // 为当前点击的阀门创建放大样式
            const status = valve.status || 1;
            const subtype = valve.subtype || 1;
            const originalStyleId = `valve_style_${subtype}_${status}`;
            const enlargedStyleId = `${originalStyleId}_enlarged`;

            // 如果放大样式不存在，则创建
            if (!updatedStyles[enlargedStyleId]) {
              const iconUrl =
                subtype === 2
                  ? ButterflyValvesIconMap[status]
                  : ValvesIconMap[status];

              updatedStyles[enlargedStyleId] = new window.TMap.MarkerStyle({
                width: 50, // 放大尺寸
                height: 57, // 放大尺寸
                anchor: { x: 25, y: 58 },
                src: iconUrl,
              });
            }
            // 返回使用放大样式的几何体
            return {
              ...geo,
              styleId: enlargedStyleId,
            };
          }
          return geo;
        });

        // 更新样式和几何体
        valveMarkersRef.current.setStyles(updatedStyles);
        valveMarkersRef.current.updateGeometries(updatedGeometries);
      }
    }
  };

  // 查看阀门详情
  const handleViewValveDetail = (valve, e) => {
    // 阻止事件冒泡，避免触发卡片的点击事件
    e.stopPropagation();

    // 如果列表是收起状态，先展开列表
    if (isListCollapsed) {
      setIsListCollapsed(false);
    }

    // 设置选中的阀门和显示详情
    setSelectedValve(valve);
    setShowDetailView(true);
  };

  // 获取用户信息
  const verifyUser = async () => {
    try {
      const result = await userService.verifyUser();
      if (result.success) {
        setCurrentUser(result.data);
      }
    } catch (error) {
      console.error("验证用户身份失败:", error);
    }
  };

  // 获取用户名列表
  const fetchNicknameOptions = async () => {
    try {
      const response = await userService.fetchNicknameOptions(2);
      if (response.code === 0) {
        setUserNames(response.data);
      } else {
        message.error(response.message || "获取用户姓名列表失败");
      }
    } catch (error) {
      console.error("获取用户姓名列表失败:", error);
    }
  };

  // 处理派单
  const handleDispatch = async (
    valveId,
    creatorId,
    assignId,
    gisPic,
    location_pic
  ) => {
    try {
      const response = await valveService.createTicket(
        valveId,
        creatorId,
        assignId,
        gisPic,
        location_pic
      );

      if (response.code === 0) {
        message.success("派单成功");

        // 获取派单处理人的昵称
        const assignUser = userNames.find((user) => user.id === assignId);
        const assignNickname = assignUser?.nickname || assignId;

        // 创建新的ticket对象
        const newTicket = {
          assign_id: assignId,
          creator_id: creatorId,
          valve_id: valveId,
          gis_pic: gisPic,
          location_pic: location_pic,
          status: 2, // 普查中
        };

        // 1. 更新当前选中的阀门状态为"普查中"并添加ticket信息
        setSelectedValve((prev) => ({
          ...prev,
          status: 2, // 普查中
          tickets: [newTicket],
        }));

        // 2. 更新阀门列表中的阀门状态和ticket信息
        setValveList((prevList) =>
          prevList.map((valve) =>
            valve.id === valveId
              ? {
                  ...valve,
                  status: 2,
                  tickets: [newTicket],
                }
              : valve
          )
        );

        // 3. 更新地图上的阀门标记
        if (valveMarkersRef.current) {
          // 获取当前所有标记
          const geometries = valveMarkersRef.current.getGeometries();

          // 找到并更新对应阀门的标记样式
          const updatedGeometries = geometries.map((geo) => {
            if (geo.properties && geo.properties.id === valveId) {
              // 更新阀门状态为普查中
              return {
                ...geo,
                styleId: `valve_style_${geo.properties.subtype || 1}_2`, // 使用普查中的样式
                properties: {
                  ...geo.properties,
                  status: 2,
                  tickets: [newTicket],
                },
              };
            }
            return geo;
          });

          // 应用更新后的标记
          valveMarkersRef.current.updateGeometries(updatedGeometries);
        }
      } else {
        message.error(response.message || "派单失败");
      }
    } catch (error) {
      message.error(
        "派单失败：" + error.response?.data?.message || error.message
      );
    }
  };

  // 处理区域筛选点击
  const handleDistrictClick = (districtId) => {
    setSelectedDistricts((prev) => {
      const newSelection = prev.includes(districtId)
        ? prev.filter((id) => id !== districtId) // 如果已选中，则移除
        : [...prev, districtId]; // 如果未选中，则添加

      // 记录当前所有筛选条件
      logCurrentFilters(newSelection, selectedStatuses);
      return newSelection;
    });
  };

  // 处理状态筛选点击
  const handleStatusClick = (statusId) => {
    setSelectedStatuses((prev) => {
      const newSelection = prev.includes(statusId)
        ? prev.filter((id) => id !== statusId) // 如果已选中，则移除
        : [...prev, statusId]; // 如果未选中，则添加

      // 记录当前所有筛选条件
      logCurrentFilters(selectedDistricts, newSelection);
      return newSelection;
    });
  };

  // 重置所有筛选条件
  const handleResetFilters = () => {
    // 清空选中的区域和状态
    setSelectedDistricts([]);
    setSelectedStatuses([]);

    // 清空筛选参数
    setFilterDistrict("");
    setFilterStatus("");

    // 更新隐藏元素的值
    document
      .getElementById("current-filter-district")
      ?.setAttribute("data-value", "");
    document
      .getElementById("current-filter-status")
      ?.setAttribute("data-value", "");

    // 重新获取阀门数据（不带筛选条件）
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const fmlshParam =
        searchType === "serialNumber" && searchValue ? searchValue.trim() : "";
      fetchValvesByGeo(center.lng, center.lat, 1000, "", "", fmlshParam);
    }
  };

  // 记录当前筛选条件并执行搜索
  const logCurrentFilters = (districts, statuses) => {
    const districtOptions = [
      { id: "district_1", name: "钟楼区" },
      { id: "district_2", name: "天宁区" },
      { id: "district_3", name: "经开区" },
      { id: "district_4", name: "新北区" },
    ];

    const statusOptions = [
      { id: "status_1", name: "待普查", value: 1 },
      { id: "status_2", name: "普查中", value: 2 },
      { id: "status_3", name: "已普查", value: 3 },
      { id: "status_4", name: "异常挂起", value: 4 },
      { id: "status_5", name: "已签证", value: 5 },
      { id: "status_6", name: "已结算", value: 6 },
      { id: "status_7", name: "已挂接", value: 7 },
    ];

    const selectedDistrictNames = districts.map(
      (id) => districtOptions.find((option) => option.id === id)?.name
    );

    const selectedStatusValues = statuses.map(
      (id) => statusOptions.find((option) => option.id === id)?.value
    );

    // 更新筛选参数
    const districtParam =
      selectedDistrictNames.length > 0 ? selectedDistrictNames.join(",") : "";
    const statusParam =
      selectedStatusValues.length > 0 ? selectedStatusValues.join(",") : "";

    setFilterDistrict(districtParam);
    setFilterStatus(statusParam);

    // 更新隐藏元素的值，以便debounce函数可以访问最新值
    document
      .getElementById("current-filter-district")
      ?.setAttribute("data-value", districtParam);
    document
      .getElementById("current-filter-status")
      ?.setAttribute("data-value", statusParam);

    // 如果地图已初始化，使用当前筛选条件重新获取阀门数据
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const fmlshParam =
        searchType === "serialNumber" && searchValue ? searchValue.trim() : "";
      fetchValvesByGeo(
        center.lng,
        center.lat,
        1000,
        districtParam,
        statusParam,
        fmlshParam
      );
    }
  };

  // 封装获取阀门数据的函数，包含筛选条件
  const fetchValvesByGeo = async (
    lng,
    lat,
    distance = 1000,
    district = "",
    status = "",
    fmlsh = ""
  ) => {
    try {
      setLoading(true);
      if (fmlsh) {
        const response = await valveService.getValveList({
          page: 1,
          limit: 100,
          fmlsh: fmlsh,
          need_geo: 1,
          version: this_Year, // 使用当前选择的年份版本
          district: district, // 添加区域筛选
          status: status // 添加状态筛选
        });
        if (response.code === 0) {
          setValveList(response.data.list || []);
          if (response.data.list && response.data.list.length > 0) {
            // 更新地图上的阀门标记
            if (mapRef.current) {
              const getscale = mapRef.current.getControl(
                window.TMap.constants.DEFAULT_CONTROL_ID.SCALE
              ).dom.innerText;
              const cleanedScale = getscale.replace(/\s+/g, "");
              let scaleInMeters;
              if (cleanedScale.includes("公里")) {
                scaleInMeters = parseFloat(cleanedScale.split("公里")[0]) * 1000;
              } else if (cleanedScale.includes("米")) {
                scaleInMeters = parseFloat(cleanedScale.split("米")[0]);
              } else {
                scaleInMeters = 0;
              }
              updateValveMarkers(mapRef.current, response.data.list, scaleInMeters);
              // 移除自动定位到第一个阀门位置的逻辑
              /* 
              // 如果有结果，将地图中心移动到第一个阀门的位置
              const firstValve = response.data.list[0];
              if (firstValve.wgs84_lat && firstValve.wgs84_lon) {
                const valvePosition = new window.TMap.LatLng(
                  firstValve.wgs84_lat,
                  firstValve.wgs84_lon
                );
                skipRequest = 1; // 设置跳过标记，防止中心点变化触发新请求
                mapRef.current.setCenter(valvePosition);
              }
              */
            }
          } else {
            message.info("未找到匹配的阀门");
            setValveList([]);
            // 清除地图上的标记
            if (valveMarkersRef.current) {
              valveMarkersRef.current.setMap(null);
            }
          }
        } else {
          message.error(response.message || "搜索失败");
        }
      } else {
        const response = await mapService.getValveListByGeo(
          lng,
          lat,
          distance,
          this_Year,
          district,
          status
        );
        if (response.code === 0) {
          setValveList(response.data);
          // 获取当前地图比例尺
          if (mapRef.current) {
            const getscale = mapRef.current.getControl(
              window.TMap.constants.DEFAULT_CONTROL_ID.SCALE
            ).dom.innerText;
            const cleanedScale = getscale.replace(/\s+/g, "");

            let scaleInMeters;
            if (cleanedScale.includes("公里")) {
              scaleInMeters = parseFloat(cleanedScale.split("公里")[0]) * 1000;
            } else if (cleanedScale.includes("米")) {
              scaleInMeters = parseFloat(cleanedScale.split("米")[0]);
            } else {
              scaleInMeters = 0;
            }
            updateValveMarkers(mapRef.current, response.data, scaleInMeters);
          }
        } else {
          message.error(response.message || "获取阀门数据失败");
        }
      }
    } catch (error) {
      console.error("获取阀门数据失败:", error);
      message.error("获取阀门数据失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout id="tencent-map">
      {/* 地图容器 */}
      <div id="map-container" className="map-container" />

      {/* 右上角操作按钮 */}
      <div className="map-control-btns">
        {/* 年份切换按钮 */}
        <div onClick={handleYearSwitch} className="switch-button year-switch">
          <img
            className="switch-icon"
            src="https://cdn.czwatersystem.com/static_res/map_layer.png"
          />
          <div className="switch-label">{currentYear}</div>
        </div>

        {/* 筛选按钮 */}
        <Popover
          placement="topLeft"
          content={
            <div className="filter-popover">
              <div className="filter-section">
                <div className="filter-title">阀门区域筛选</div>
                <div className="filter-options">
                  {[
                    { id: "district_1", name: "钟楼区" },
                    { id: "district_2", name: "天宁区" },
                    { id: "district_3", name: "经开区" },
                    { id: "district_4", name: "新北区" },
                  ].map((district) => (
                    <div
                      key={district.id}
                      className={`filter-option ${
                        selectedDistricts.includes(district.id)
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleDistrictClick(district.id)}
                    >
                      {district.name}
                    </div>
                  ))}
                </div>
              </div>

              <div className="filter-section">
                <div className="filter-title">阀门状态筛选</div>
                <div className="filter-options">
                  {[
                    { id: "status_1", name: "待普查", value: 1 },
                    { id: "status_2", name: "普查中", value: 2 },
                    { id: "status_3", name: "已普查", value: 3 },
                    { id: "status_4", name: "异常挂起", value: 4 },
                    { id: "status_5", name: "已签证", value: 5 },
                    { id: "status_6", name: "已结算", value: 6 },
                    { id: "status_7", name: "已挂接", value: 7 },
                  ].map((status) => (
                    <div
                      key={status.id}
                      className={`filter-option ${
                        selectedStatuses.includes(status.id) ? "selected" : ""
                      }`}
                      onClick={() => handleStatusClick(status.id)}
                    >
                      {status.name}
                    </div>
                  ))}
                </div>
              </div>

              <div className="filter-actions">
                <Button
                  type="primary"
                  onClick={handleResetFilters}
                  className="reset-filter-btn"
                >
                  重置筛选
                </Button>
              </div>
            </div>
          }
          trigger="click"
        >
          <div className="switch-button">
            <img
              className="switch-icon"
              src="https://cdn.czwatersystem.com/static_res/map_filter.png"
              alt="筛选"
            />
            <div className="switch-label">筛选</div>
          </div>
        </Popover>
      </div>

      <div
        className={`valve-list-container ${isListCollapsed ? "collapsed" : ""}`}
      >
        {showDetailView && selectedValve
          ? [
              <div className="detail-header">
                <div className="back-button">
                  <Button type="link" onClick={() => setShowDetailView(false)}>
                    返回列表
                  </Button>
                </div>
                <div className="header-title">阀门详情</div>
                {selectedValve.status === 1 && (
                  <div className="action-button">
                    <Button
                      type="primary"
                      size="small"
                      style={{
                        background: "linear-gradient(180deg, #339DE2 0%, #1763C1 100%)",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#FFFFFF",
                      }}
                      onClick={() => {
                        setDispatchValve({
                          ...selectedValve,
                          location_pic: selectedValve.gis_pic,
                        });
                        setIsDispatchModalVisible(true);
                      }}
                    >
                      派单
                    </Button>
                  </div>
                )}
              </div>,
              <div className="scroll-view">
                <div className="valve-detail-view">
                  <div className="detail-card">
                    {/* 字段行样式 */}
                    <div className="detail-row">
                      <div className="detail-label">阀门ID</div>
                      <div className="detail-value">
                        {selectedValve.id || "-"}
                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label">GIS流水号</div>
                      <div className="detail-value">
                        {selectedValve.fmlsh}
                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label">X坐标</div>
                      <div className="detail-value">
                        {selectedValve.geometry_x ? Number(selectedValve.geometry_x).toFixed(3) : "-"}
                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label">Y坐标</div>
                      <div className="detail-value">
                      {selectedValve.geometry_y ? Number(selectedValve.geometry_y).toFixed(3) : "-"}

                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label">Z坐标</div>
                      <div className="detail-value">
                      {selectedValve.geometry_z ? Number(selectedValve.geometry_z).toFixed(3) : "-"}

                      </div>
                    </div>
                    <div className="detail-row">
                      <div className="detail-label">阀门状态</div>
                      <div className="detail-value">
                        <span
                          style={{
                            color:
                              valveStatusConfig[selectedValve.status]?.color,
                          }}
                        >
                          {valveStatusConfig[selectedValve.status]?.text || "-"}
                        </span>
                      </div>
                    </div>

                    {/* GIS位置图 */}
                    <div className="detail-row">
                      <div className="detail-label">GIS位置图</div>
                      <div className="detail-value">
                        <img
                          src={selectedValve.gis_pic}
                          className="gis-image"
                        />
                      </div>
                    </div>
                    
                  </div>
                  <div className="detail-card">
                    {/* 普查日期 */}
                    <div className="detail-row">
                      <div className="detail-label">普查日期</div>
                      <div className="detail-value">
                        {selectedValve.pc_date}
                      </div>
                    </div>

                    {/* 所属区域 */}
                    <div className="detail-row">
                      <div className="detail-label">所属区域</div>
                      <div className="detail-value">
                        {selectedValve.district || "-"}
                      </div>
                    </div>

                    {/* 地址 */}
                    <div className="detail-row">
                      <div className="detail-label">地址</div>
                      <div className="detail-value">
                        {selectedValve.address || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="detail-card">
                    {/* 阀门类型 */}
                    <div className="detail-row">
                      <div className="detail-label">阀门类型</div>
                      <div className="detail-value">
                        {valveTypeMap[selectedValve.subtype] || "-"}
                      </div>
                    </div>

                    {/* 阀门深度 */}
                    <div className="detail-row">
                      <div className="detail-label">阀门深度</div>
                      <div className="detail-value">
                        {selectedValve.depth || "-"}
                      </div>
                    </div>

                    {/* 阀门类别 */}
                    <div className="detail-row">
                      <div className="detail-label">阀门类别</div>
                      <div className="detail-value">
                        {valveCategoryMap[selectedValve.category] || "-"}
                      </div>
                    </div>

                    {/* 阀门口径 */}
                    <div className="detail-row">
                      <div className="detail-label">阀门口径</div>
                      <div className="detail-value">
                        {selectedValve.standard || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="detail-card">
                    {/* 井盖编号 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖编号</div>
                      <div className="detail-value">
                        {selectedValve.jgbh || "-"}
                      </div>
                    </div>

                    {/* 井盖厂家 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖厂家</div>
                      <div className="detail-value">
                        {selectedValve.jgcj || "-"}
                      </div>
                    </div>

                    {/* 井盖尺寸 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖尺寸</div>
                      <div className="detail-value">
                        {selectedValve.jgcc || "-"}
                      </div>
                    </div>

                    {/* 井盖材质 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖材质</div>
                      <div className="detail-value">
                        {selectedValve.jgcz || "-"}
                      </div>
                    </div>

                    {/* 井盖状态 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖状态</div>
                      <div className="detail-value">
                        {selectedValve.jgzt === null
                          ? "-"
                          : selectedValve.fmzt === 1
                          ? "完好"
                          : "破损"}
                      </div>
                    </div>

                    {/* 井盖周边 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖周边</div>
                      <div className="detail-value">
                        {selectedValve.jgzb === null
                          ? "-"
                          : selectedValve.fmzt === 1
                          ? "完好"
                          : "破损"}
                      </div>
                    </div>

                    {/* 井盖位置 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖位置</div>
                      <div className="detail-value">
                        {wellCoverLocationsMap[selectedValve.jgwz] || "-"}
                      </div>
                    </div>

                    {/* 井盖异常描述 */}
                    <div className="detail-row">
                      <div className="detail-label">井盖异常描述</div>
                      <div className="detail-value">
                        {wellCoverAbnormalDescMap[selectedValve.jgycms] || "-"}
                      </div>
                    </div>

                    {/* 查验类别 */}
                    <div className="detail-row">
                      <div className="detail-label">查验类别</div>
                      <div className="detail-value">
                        {cylbDescMap[selectedValve.cylb] || "-"}
                      </div>
                    </div>

                    {/* 照片 */}
                    <div className="detail-row detail-row-photo">
                      <div className="detail-label">照片</div>
                      <div className="detail-value">
                        <div className="photo-container">
                          <div className="photo-item">
                            <img
                              src={selectedValve.jg_pic_near}
                              className="photo-image"
                            />
                            <div className="photo-caption">井内近景</div>
                          </div>
                          <div className="photo-item">
                            <img
                              src={selectedValve.jg_pic_far}
                              className="photo-image"
                            />
                            <div className="photo-caption">井盖远景</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 备注 */}
                    <div className="detail-row">
                      <div className="detail-label">备注</div>
                      <div className="detail-value">
                        {selectedValve.memo || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>,
            ]
          : [
              <div className="search-bar">
                <Select
                  defaultValue="address"
                  options={[
                    { value: "address", label: "地址" },
                    { value: "serialNumber", label: "流水号" },
                  ]}
                  className="search-type-select"
                  onChange={(value) => {
                    setSearchType(value);
                    // 更新隐藏元素的值
                    document
                      .getElementById("current-search-type")
                      ?.setAttribute("data-value", value);
                  }}
                />
                <Input
                  placeholder="请输入搜索内容"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    // 更新隐藏元素的值
                    document
                      .getElementById("current-search-value")
                      ?.setAttribute("data-value", e.target.value);
                  }}
                  className="search-input"
                />
                {Boolean(searchValue?.length) && (
                  <Button
                    className="reset-btn"
                    icon={<CloseCircleOutlined/>}
                    variant="link"
                    color="danger"
                    onClick={() => {
                      // 清空搜索词
                      setSearchValue("");
                      document
                        .getElementById("current-search-value")
                        ?.setAttribute("data-value", "");

                      // 重新获取阀门数据
                      if (mapRef.current) {
                        const center = mapRef.current.getCenter();
                        fetchValvesByGeo(
                          center.lng,
                          center.lat,
                          1000,
                          filterDistrict,
                          filterStatus,
                          ""
                        );
                      }
                    }}
                  ></Button>
                )}
                <Button
                  type="primary"
                  className="search-btn"
                  onClick={async () => {
                    if (!searchValue.trim()) {
                      message.warning("请输入搜索内容");
                      return;
                    }
                    try {
                      setLoading(true);

                      if (searchType === "serialNumber") {
                        // 如果是流水号搜索，使用valveService.getValveList方法
                        if (searchValue.trim()) {
                          const fmlshParam = searchValue.trim();
                          const response = await valveService.getValveList({
                            page: 1,
                            limit: 100,
                            fmlsh: fmlshParam,
                            need_geo: 1,
                            version: this_Year, // 使用当前选择的年份版本
                            district: filterDistrict, // 添加区域筛选
                            status: filterStatus // 添加状态筛选
                          });
                          if (response.code === 0) {
                            setValveList(response.data.list || []);
                            if (response.data.list && response.data.list.length > 0) {
                              // 更新地图上的阀门标记
                              if (mapRef.current) {
                                const getscale = mapRef.current.getControl(
                                  window.TMap.constants.DEFAULT_CONTROL_ID.SCALE
                                ).dom.innerText;
                                const cleanedScale = getscale.replace(/\s+/g, "");
                                let scaleInMeters;
                                if (cleanedScale.includes("公里")) {
                                  scaleInMeters = parseFloat(cleanedScale.split("公里")[0]) * 1000;
                                } else if (cleanedScale.includes("米")) {
                                  scaleInMeters = parseFloat(cleanedScale.split("米")[0]);
                                } else {
                                  scaleInMeters = 0;
                                }
                                updateValveMarkers(mapRef.current, response.data.list, scaleInMeters);
                                // 移除自动定位到第一个阀门位置的逻辑
                                /* 
                                // 如果有结果，将地图中心移动到第一个阀门的位置
                                const firstValve = response.data.list[0];
                                if (firstValve.wgs84_lat && firstValve.wgs84_lon) {
                                  const valvePosition = new window.TMap.LatLng(
                                    firstValve.wgs84_lat,
                                    firstValve.wgs84_lon
                                  );
                                  skipRequest = 1; // 设置跳过标记，防止中心点变化触发新请求
                                  mapRef.current.setCenter(valvePosition);
                                }
                                */
                              }
                            } else {
                              message.info("未找到匹配的阀门");
                              setValveList([]);
                              // 清除地图上的标记
                              if (valveMarkersRef.current) {
                                valveMarkersRef.current.setMap(null);
                              }
                            }
                          } else {
                            message.error(response.message || "搜索失败");
                          }
                        } else {
                          message.warning("请输入阀门流水号");
                        }
                      } else {
                        // 如果是地址搜索，使用腾讯地图suggestion API
                        const response = await mapService.searchValves(
                          searchValue.trim()
                        );

                        if (response.status === 0) {
                          // 腾讯地图API返回成功
                          if (response.data && response.data.length > 0) {
                            // 过滤出常州市的数据
                            const czSearchResults = response.data.filter(
                              (item) => item.city === "常州市"
                            );

                            if (czSearchResults.length > 0) {
                              // 设置搜索结果，添加标记表示这是搜索结果而非阀门
                              const searchResultsList = czSearchResults.map(
                                (item) => ({
                                  id: item.id,
                                  title: item.title,
                                  address: item.address,
                                  category: item.category,
                                  district: item.district,
                                  province: item.province,
                                  city: item.city,
                                  location: item.location,
                                  is_search_result: true, // 标记这是搜索结果
                                })
                              );

                              setValveList(searchResultsList);

                              // 不再自动将地图中心移动到第一个结果的位置
                            } else {
                              message.info("未找到常州市内匹配的地址");
                              setValveList([]);
                            }
                          } else {
                            message.info("未找到匹配的地址");
                            setValveList([]);
                          }
                        } else {
                          message.error(response.message || "搜索失败");
                        }
                      }
                    } catch (error) {
                      console.error("搜索失败:", error);
                      message.error("搜索失败");
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  搜索
                </Button>
              </div>,
              <div className="valve-list-title">
                <div className="label">当前阀门区域{" "}</div>
                <div
                  className="fold-btn"
                  onClick={() => setIsListCollapsed(!isListCollapsed)}
                >
                  <img className="icon" src="https://cdn.czwatersystem.com/static_res/arrow_right_primary.png"></img>
                  收起列表
                </div>
              </div>,
              <div className="scroll-view">
                {loading ? (
                  <div className="loading-container">
                    <Spin tip="加载中..." />
                  </div>
                ) : (
                  <List
                    itemLayout="vertical"
                    dataSource={valveList}
                    className="valve-list-content"
                    header={
                      valveList.length > 0 && valveList[0].is_search_result ? (
                        <div className="list-header-tip">
                          <div className="tip-text search-tip">
                            点击前往对应位置并搜索阀门
                          </div>
                        </div>
                      ) : null
                    }
                    renderItem={(item) =>
                      // 根据是否为搜索结果使用不同的渲染方式
                      item.is_search_result ? (
                        // 搜索结果卡片样式
                        <List.Item
                          key={item.id}
                          onClick={async () => {
                            // 1. 点击时打印位置信息
                            console.log("搜索结果位置信息:", item.location);

                            if (mapRef.current && item.location) {
                              // 2. 移动地图到该位置
                              skipRequest = 1; // 防止地图移动触发搜索
                              mapRef.current.setCenter(
                                new window.TMap.LatLng(
                                  item.location.lat,
                                  item.location.lng
                                )
                              );

                              try {
                                // 3. 在该位置搜索阀门
                                setLoading(true);

                                // 获取当前筛选条件
                                const currentFilterDistrict =
                                  document
                                    .getElementById("current-filter-district")
                                    ?.getAttribute("data-value") || "";
                                const currentFilterStatus =
                                  document
                                    .getElementById("current-filter-status")
                                    ?.getAttribute("data-value") || "";

                                // 调用API获取阀门数据
                                await fetchValvesByGeo(
                                  item.location.lng,
                                  item.location.lat,
                                  1000,
                                  currentFilterDistrict,
                                  currentFilterStatus,
                                  ""
                                );
                              } catch (error) {
                                console.error("获取阀门数据失败:", error);
                                message.error("获取阀门数据失败");
                              } finally {
                                setLoading(false);
                              }
                            }
                          }}
                          className="search-result-item"
                        >
                          <div className="search-item-header">
                            <div className="search-item-title">
                              {item.title}
                            </div>
                          </div>
                          <div className="search-item-address">
                            {item.address}
                          </div>
                          <div className="search-item-location">
                            {item.category
                              ? item.category.split(":")[1] || item.category
                              : ""}
                          </div>
                        </List.Item>
                      ) : (
                        // 原有的阀门卡片样式
                        <List.Item
                          key={item.id}
                          onClick={() => handleValveClick(item)}
                          className={`valve-item ${
                            dispatchValve?.id === item.id ? "selected" : ""
                          }`}
                        >
                          <div
                            className="valve-item-row"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <div style={{ fontWeight: "bold" }}>
                              GIS流水号 : <span>{item.fmlsh}</span>
                            </div>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <span
                                style={{
                                  marginLeft: "10px",
                                  padding: "2px 8px",
                                  borderRadius: "20px",
                                  backgroundColor:
                                    valveStatusConfig[item.status]?.color,
                                  color: "#fff",
                                  fontSize: "12px",
                                }}
                              >
                                {valveStatusConfig[item.status]?.text}
                              </span>
                            </div>
                          </div>

                          <div
                            className="valve-item-row"
                            style={{ marginBottom: "8px" }}
                          >
                            <div className="value">{item.address}</div>
                          </div>

                          {/* 根据状态显示不同的操作按钮 */}
                          <div
                            className="valve-item-actions"
                            style={{
                              marginTop: "8px",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {/* 待普查状态 - 显示派单和查看详情按钮 */}
                            {item.status === 1 && (
                              <>
                                <Button
                                  type="primary"
                                  size="small"
                                  style={{
                                    width: "157px",
                                    height: "28px",
                                    background:
                                      "linear-gradient( 180deg, #339DE2 0%, #1763C1 100%)",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    color: "#FFFFFF",
                                    lineHeight: "16px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDispatchValve({
                                      ...item,
                                      location_pic: item.gis_pic,
                                    });
                                    setIsDispatchModalVisible(true);
                                  }}
                                >
                                  派单
                                </Button>
                                <Button
                                  type="default"
                                  size="small"
                                  style={{
                                    width: "157px",
                                    height: "28px",
                                    background: "rgba(255,255,255,0.15)",
                                    borderRadius: "4px",
                                    border: "1px solid #1763C1",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    color: "#1763C1",
                                    lineHeight: "16px",
                                  }}
                                  onClick={(e) =>
                                    handleViewValveDetail(item, e)
                                  }
                                >
                                  查看阀门详情
                                </Button>
                              </>
                            )}

                            {/* 普查中状态 - 显示处理人和查看详情按钮 */}
                            {item.status === 2 && (
                              <>
                                <Button
                                  style={{
                                    width: "157px",
                                    height: "28px",
                                    background: "#F2F2F7",
                                    borderRadius: "4px",
                                    border: "1px solid #F2F2F7",
                                    fontSize: "14px",
                                    color: "#000000",
                                    lineHeight: "16px",
                                    textAlign: "center",
                                  }}
                                >
                                  处理人:
                                  {userNames.find(
                                    (user) =>
                                      user.id === item.tickets[0]?.assign_id
                                  )?.nickname ||
                                    item.tickets[0]?.assign_id ||
                                    "-"}
                                </Button>
                                <Button
                                  type="default"
                                  size="small"
                                  style={{
                                    width: "157px",
                                    height: "28px",
                                    background: "rgba(255,255,255,0.15)",
                                    borderRadius: "4px",
                                    border: "1px solid #1763C1",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    color: "#1763C1",
                                    lineHeight: "16px",
                                  }}
                                  onClick={(e) =>
                                    handleViewValveDetail(item, e)
                                  }
                                >
                                  查看阀门详情
                                </Button>
                              </>
                            )}

                            {/* 其他状态 - 只显示查看详情按钮 */}
                            {item.status !== 1 && item.status !== 2 && (
                              <Button
                                type="default"
                                size="small"
                                style={{
                                  width: "324px",
                                  height: "28px",
                                  background: "rgba(255,255,255,0.15)",
                                  borderRadius: "4px",
                                  border: "1px solid #1763C1",
                                  fontWeight: "600",
                                  fontSize: "14px",
                                  color: "#1763C1",
                                  lineHeight: "16px",
                                }}
                                onClick={(e) => handleViewValveDetail(item, e)}
                              >
                                查看阀门详情
                              </Button>
                            )}
                          </div>
                        </List.Item>
                      )
                    }
                  />
                )}
              </div>,
            ]}
      </div>
      {isListCollapsed && (
        <div
          className="expand-list-button"
          onClick={() => setIsListCollapsed(false)}
        ></div>
      )}

      <Modal
        title="派单信息"
        open={isDispatchModalVisible}
        onOk={() => {
          if (!selectedUser) {
            message.error("请选择处理人");
            return;
          }
          handleDispatch(
            dispatchValve?.id,
            currentUser?.userId,
            selectedUser,
            dispatchValve?.gis_pic,
            dispatchValve?.location_pic
          );
          setIsDispatchModalVisible(false);
          setSelectedUser(null);
          setDispatchValve(null);
        }}
        onCancel={() => {
          setIsDispatchModalVisible(false);
          setSelectedUser(null);
          setDispatchValve(null);
        }}
        destroyOnClose
        okText="确认派单"
        cancelText="取消"
        width={550}
        okButtonProps={{ disabled: uploading }}
      >
        <div style={{ marginBottom: "16px" }}>
          <div>
            <strong>阀门ID：</strong>
            {dispatchValve?.id}
          </div>
          <div>
            <strong>创建人：</strong>
            {currentUser?.nickname || "-"}
          </div>
          <div style={{ paddingTop: "10px", marginBottom: "16px" }}>
            <strong>GIS位置图：</strong>
            <PasteImage
              style={{ display: "inline-block" }}
              src={selectedValve?.location_pic}
              onSuccess={(result) => {
                setDispatchValve((prev) => ({
                  ...prev,
                  location_pic: result.url,
                }));
              }}
              onUploadStatusChange={setUploading}
            ></PasteImage>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <strong>GIS平面图：</strong>
            <PasteImage
              style={{ display: "inline-block" }}
              src={selectedValve?.gis_pic}
              onSuccess={(result) => {
                setDispatchValve((prev) => ({
                  ...prev,
                  gis_pic: result.url,
                }));
              }}
              onUploadStatusChange={setUploading}
            ></PasteImage>
          </div>
          <div>
            <strong>处理人：</strong>
            <Select
              style={{ width: "200px", marginLeft: "8px" }}
              placeholder="请选择处理人"
              value={selectedUser}
              onChange={(value) => setSelectedUser(value)}
            >
              {userNames.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.nickname}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
      {/* 隐藏元素，用于存储当前筛选和搜索值 */}
      <div style={{ display: "none" }}>
        <div id="current-filter-district" data-value={filterDistrict}></div>
        <div id="current-filter-status" data-value={filterStatus}></div>
        <div id="current-search-type" data-value={searchType}></div>
        <div id="current-search-value" data-value={searchValue}></div>
      </div>
    </Layout>
  );
}

export default TencentMap;
